/**
 * Shared, deduped canvas sandbox provision.
 *
 * The token is canvas-scoped (internal:sandbox-terminal + sandbox-scope:canvas +
 * sandbox-resource-id:<canvasId>) and the sandbox is one-per-(org, user, canvas), so the
 * {terminal_server_url, terminal_server_token} pair is identical for every terminal widget on a
 * canvas. The only per-widget value is session_id, which lives on the WS URL, never in the provision
 * call. So we make at most one provision (one sandbox + one token mint) per canvas and share it
 * across all widgets, instead of one per widget per reconnect.
 */
import type { CanvasSandboxProvisionResponse } from "@cohesive/api-client";

import { orgAPI } from "@/api/api";

interface CacheEntry {
  promise: Promise<CanvasSandboxProvisionResponse>;
  // ms epoch when the cached value goes stale; 0 while the request is still in flight.
  expiresAt: number;
}

// Refresh comfortably before the server's 5-min token TTL so a cached token always has runway to
// open a WebSocket.
const PROVISION_TTL_MS = 4 * 60 * 1000;

const cache = new Map<string, CacheEntry>();

// Per-canvas count of terminal widgets currently using the provision. When the last widget on a
// canvas unmounts we drop its cached {url, token} so a short-lived internal token doesn't linger in
// memory after you leave the canvas. (Logout/login do a full Auth0 redirect that wipes all module
// state anyway; this covers in-app navigation away from a canvas without a reload.)
const refcounts = new Map<string, number>();

function cacheKey(orgId: string, userId: string, canvasId: string): string {
  return `${orgId}:${userId}:${canvasId}`;
}

/**
 * Returns the shared {terminal_server_url, terminal_server_token} for a canvas, making at most one
 * provision request per canvas per TTL. Concurrent callers share one in-flight request; a fresh
 * cached value returns with no request. A failed request is not cached.
 *
 * orgId/userId are part of the cache key (and the token's identity) but the api-client derives them
 * from the client, so the request itself only needs the canvasId. session_id / cols / rows are
 * per-widget and unused by provisioning — each widget puts those on its own WS URL.
 */
export function getCanvasSandboxProvision(
  orgId: string,
  userId: string,
  canvasId: string,
): Promise<CanvasSandboxProvisionResponse> {
  const key = cacheKey(orgId, userId, canvasId);
  const entry = cache.get(key);
  if (entry && (entry.expiresAt === 0 || entry.expiresAt > Date.now())) {
    return entry.promise;
  }

  const fresh: CacheEntry = {
    promise: orgAPI.canvas.sandboxProvision(canvasId),
    expiresAt: 0,
  };
  cache.set(key, fresh);
  fresh.promise.then(
    () => {
      // Stamp the TTL only if this entry is still current (not invalidated mid-flight).
      if (cache.get(key) === fresh) {
        fresh.expiresAt = Date.now() + PROVISION_TTL_MS;
      }
    },
    () => {
      // Drop a rejected request so the next caller retries instead of reusing the failure.
      if (cache.get(key) === fresh) cache.delete(key);
    },
  );
  return fresh.promise;
}

/**
 * Forces the next getCanvasSandboxProvision to re-provision (+ re-mint). Call before a reconnect:
 * the cached terminal server URL/token go stale when the sandbox is paused, reset, or rejects the
 * token (1008), and only a fresh provision re-provisions it.
 */
export function invalidateCanvasSandboxProvision(
  orgId: string,
  userId: string,
  canvasId: string,
): void {
  cache.delete(cacheKey(orgId, userId, canvasId));
}

/** Register a terminal widget as a user of the canvas's provision (call on mount). */
export function retainCanvasSandboxProvision(
  orgId: string,
  userId: string,
  canvasId: string,
): void {
  const key = cacheKey(orgId, userId, canvasId);
  refcounts.set(key, (refcounts.get(key) ?? 0) + 1);
}

/**
 * Release a terminal widget's use of the canvas's provision (call on unmount). When the last user
 * is gone, the cached {url, token} is dropped so the token doesn't outlive the canvas in memory.
 */
export function releaseCanvasSandboxProvision(
  orgId: string,
  userId: string,
  canvasId: string,
): void {
  const key = cacheKey(orgId, userId, canvasId);
  const remaining = (refcounts.get(key) ?? 1) - 1;
  if (remaining <= 0) {
    refcounts.delete(key);
    // Drop a SETTLED token so it doesn't linger after the last widget leaves. Leave an in-flight
    // request (expiresAt === 0) alone: deleting it orphans a POST that still completes and mints a
    // token server-side, wasting it. Letting it finish stamps its TTL so a re-entry (or a widget
    // whose connect() is mid-await and hasn't retained yet) reuses it instead of re-provisioning.
    const entry = cache.get(key);
    if (entry && entry.expiresAt !== 0) cache.delete(key);
  } else {
    refcounts.set(key, remaining);
  }
}

/** Drop every cached provision — call on logout / auth teardown. */
export function clearSandboxProvisionCache(): void {
  cache.clear();
  refcounts.clear();
}
